<script setup lang="ts">
import { computed, reactive } from 'vue';

import {
  type Context,
  type Note as NoteInterface,
  ToolLayoutKind,
  ToolPosition,
} from '@thehotelsnetwork/personalization-types';
import { Note, devicePositionedLayouts } from '@thehotelsnetwork/personalization-core';

type ToolPayload = {
  tool: NoteInterface;
  context: Context;
}

type ContainerMapData = {
  slideInTools: ToolPayload[];
  barTools: ToolPayload[];
  visible: boolean;
}

const containerMap = reactive(
  Object.values(ToolPosition).reduce(
    (acc, toolPosition) => ({
      ...acc,
      [toolPosition]: { slideInTools: [], barTools: [], visible: false },
    }),
    {} as Record<ToolPosition, ContainerMapData>,
  ),
);

const visibleContainers = computed(() => (
  (Object.keys(containerMap) as ToolPosition[]).filter((pos) => containerMap[pos]?.visible)
));

function removeNote(note: NoteInterface) {
  const layout = note.settings.layout?.type || ToolLayoutKind.SlideIn;
  const position = devicePositionedLayouts.includes(layout)
    ? note.settings.devices.desktop.position
    : note.settings.layout?.position;

  if (!position) return;

  setTimeout(() => {
    if (layout === ToolLayoutKind.SlideIn) {
      containerMap[position].slideInTools = containerMap[position].slideInTools.filter(
        (payload) => payload.tool.id !== note.id,
      );
    } else {
      containerMap[position].barTools = containerMap[position].barTools.filter(
        (payload) => payload.tool.id !== note.id,
      );
    }
  }, 500);
}

const isRtlDir = computed(() => window.getComputedStyle(document.body)?.direction === 'rtl');
const effectiveRightContainerAlignment = computed(() => (isRtlDir.value ? 'flex-start' : 'flex-end'));
const effectiveLeftContainerAlignment = computed(() => (isRtlDir.value ? 'flex-end' : 'flex-start'));

function addNote(notePayload: { tool: NoteInterface, context: Context }) {
  const { tool, context } = notePayload;

  const layout = tool.settings.layout?.type || ToolLayoutKind.SlideIn;

  const position = devicePositionedLayouts.includes(layout)
    ? tool.settings.devices[context.deviceViewport].position
    : tool.settings.layout?.position;

  if (position) {
    // Show container in case it's not visible yet
    if (!containerMap[position].visible) {
      containerMap[position].visible = true;
    }

    if (layout === ToolLayoutKind.SlideIn) {
      containerMap[position].slideInTools.push(notePayload);
    } else {
      containerMap[position].barTools.push(notePayload);
    }
  }
  return removeNote.bind(null, tool);
}

defineExpose({
  addNote,
  removeNote,
});

</script>

<template>
  <div
    v-for="position in visibleContainers"
    :key="position"
    :class="`container container__${position}`"
    :data-position="position"
    :data-testid="position"
  >
    <div class="container__slideIn">
      <Note
        v-for="noteProps in containerMap[position].slideInTools"
        :key="noteProps.tool.id"
        v-bind="noteProps as any"
        @hide="removeNote(noteProps.tool)"
      />
    </div>

    <div class="container__bar">
      <Note
        v-for="noteProps in containerMap[position].barTools"
        :key="noteProps.tool.id"
        v-bind="noteProps as any"
        @hide="removeNote(noteProps.tool)"
      />
    </div>
  </div>
</template>

<style lang="postcss">
.container {
  --container-gap: 16px;
  position: fixed;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: var(--container-gap);
  pointer-events: none;
  .thn--tool {
    pointer-events: auto;
    blockquote, dd, dl, figure, h1, h2, h3, h4, h5, h6, hr, p, pre {
      margin: 0;
    }
  }

  /* Contenedor específico para SlideIn */
  .container__slideIn {
    --container-gap: 16px;
    gap: var(--container-gap);
    margin-block: max(1.5vw, 2vh);
  }

  /* Contenedor específico para Bar */
  .container__bar {
    width: 100%;
  }

  .container__bar,
  .container__slideIn {
    display: flex;
    flex-direction: column;
    pointer-events: none;
  }

  &[data-position="UL"] {
    top: 0;
    left: 0;
  }

  &[data-position="UML"] {
    top: 20%;
    left: 0;
  }

  &[data-position="LML"] {
    bottom: 20%;
    left: 0;
  }

  &[data-position="LL"] {
    bottom: 0;
    left: 0;
  }

  /* Right */
  &[data-position="UR"] {
    top: 0;
    right: 0;
  }

  &[data-position="UMR"] {
    top: 20%;
    right: 0;
  }

  &[data-position="LMR"] {
    bottom: 20%;
    right: 0;
  }

  &[data-position="LR"] {
    bottom: 0;
    right: 0;
  }

  &[data-position="T"] {
    top: 0;
    width: 100%;
    align-items: center;
    padding-right: 0;
    padding-left: 0;
  }

  &[data-position="B"] {
    bottom: 0;
    width: 100%;
    align-items: center;
    padding-right: 0;
    padding-left: 0;
  }

  /* For all the containers positioned on the right side */
  &[data-position$="R"] {
    align-items: v-bind(effectiveRightContainerAlignment);
  }

  /* For all the containers positioned on the left side */
  &[data-position$="L"] {
    align-items: v-bind(effectiveLeftContainerAlignment);
  }
}
</style>
